import React from 'react';
import styled, { keyframes } from 'styled-components';
import { useLocation } from 'react-router-dom';
import { FaPhone, FaWhatsapp, FaSms } from 'react-icons/fa';
import { useLanguage } from '../../hooks/useLanguage';
import { getTranslation } from '../../translations/presentation';
import {
  ChannelsTitle,
  ChannelsContainer,
  ChannelItem,
  ChannelIconContainer,
  ChannelIcon,
  ChannelName
} from '../../components/ChannelsStyles';

// Animações
const pulseGlow = keyframes`
  0% {
    box-shadow: 0 0 5px rgba(100, 182, 255, 0.5), 0 0 10px rgba(100, 182, 255, 0.3);
    border-color: rgba(100, 182, 255, 0.3);
  }
  50% {
    box-shadow: 0 0 25px rgba(100, 182, 255, 0.8), 0 0 40px rgba(100, 182, 255, 0.5);
    border-color: rgba(0, 255, 255, 0.8);
  }
  100% {
    box-shadow: 0 0 5px rgba(100, 182, 255, 0.5), 0 0 10px rgba(100, 182, 255, 0.3);
    border-color: rgba(100, 182, 255, 0.3);
  }
`;



const matrixReveal = keyframes`
  0% {
    opacity: 0;
    transform: perspective(1000px) rotateX(10deg) translateY(50px);
    clip-path: polygon(0 0, 100% 0, 100% 0, 0 0);
  }
  30% {
    clip-path: polygon(0 0, 100% 0, 100% 20%, 0 20%);
  }
  60% {
    clip-path: polygon(0 0, 100% 0, 100% 60%, 0 60%);
  }
  100% {
    opacity: 1;
    transform: perspective(1000px) rotateX(0deg) translateY(0);
    clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%);
  }
`;

const circleExpand = keyframes`
  0% {
    clip-path: circle(0% at center);
    opacity: 0;
  }
  100% {
    clip-path: circle(150% at center);
    opacity: 1;
  }
`;

const floatAnimation = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-5px);
  }
`;


const rotateIn = keyframes`
  0% {
    transform: rotate(180deg) scale(0);
    opacity: 0;
  }
  100% {
    transform: rotate(0deg) scale(1);
    opacity: 1;
  }
`;


const holographicShimmer = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const energyBurst = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.6);
    filter: blur(10px);
  }
  50% {
    opacity: 0.8;
    filter: blur(5px);
  }
  100% {
    opacity: 0;
    transform: scale(1.4);
    filter: blur(0px);
  }
`;

const iconPop = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
`;

const phoneIconPop = keyframes`
  0% {
    transform: scaleX(-1) scale(1);
  }
  50% {
    transform: scaleX(-1) scale(1.2);
  }
  100% {
    transform: scaleX(-1) scale(1);
  }
`;

const smsIconPop = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
`;

const SlideWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  padding: 20px;
  overflow: hidden;
  position: relative;
  
`;

const SlideTitle = styled.h2`
  font-family: 'Orbitron', sans-serif;
  color: transparent;
  margin-bottom: 30px;
  text-align: center;
  letter-spacing: 3px;
  font-size: 2.2rem;
  text-transform: uppercase;
  position: absolute;
  top: 30px;
  left: 0;
  right: 0;
  background: linear-gradient(90deg, #64B6FF, #00FFFF, #90CAFF);
  background-size: 200% auto;
  -webkit-background-clip: text;
  background-clip: text;
  background-size: 200% auto;
  animation: ${holographicShimmer} 6s linear infinite;
  font-weight: 600;
  z-index: 10;
  
  &::before, &::after {
    content: '';
    position: absolute;
    height: 2px;
    width: 100px;
    background: linear-gradient(90deg, transparent, rgba(0, 255, 255, 0.8), transparent);
    left: 50%;
    transform: translateX(-50%);
  }
  
  &::before {
    top: -10px;
  }
  
  &::after {
    bottom: -10px;
  }
`;

const TitleCodeDecoration = styled.span`
  position: absolute;
  font-family: monospace;
  font-size: 12px;
  color: rgba(100, 182, 255, 0.5);
  opacity: 0.6;
  
  &.top-left {
    top: -10px;
    left: 20%;
    &::before {
      content: '/* channels.initialize() */';
    }
  }
  
  &.top-right {
    top: -10px;
    right: 20%;
    &::before {
      content: '/* bianca.core */';
    }
  }
`;

const ContentPreview = styled.div`
  max-width: 1200px;
  width: 90%;
  height: 65vh;
  margin: 0 auto;
  padding: 60px;
  border: 1px solid rgba(100, 182, 255, 0.3);
  border-radius: 15px;
  background: rgba(5, 10, 25, 0.7);
  box-shadow: 0 0 30px rgba(100, 182, 255, 0.15);
  backdrop-filter: blur(10px);
  position: relative;
  animation: ${matrixReveal} 1.2s cubic-bezier(0.19, 1, 0.22, 1) forwards;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  
  &::before, &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, rgba(0, 255, 255, 0.8), transparent);
    animation: ${pulseGlow} 5s infinite;
  }
  
  &::after {
    top: auto;
    bottom: 0;
    animation-delay: 1.5s;
  }
`;


const EnhancedChannelsTitle = styled(ChannelsTitle)`
  font-size: 2.5rem;
  margin-bottom: 50px;
  background: linear-gradient(90deg, #64B6FF, #00FFFF, #64B6FF);
  background-size: 200% auto;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  opacity: 0;
  animation: ${circleExpand} 0.8s cubic-bezier(0.19, 1, 0.22, 1) forwards 0.5s, 
            ${holographicShimmer} 5s linear infinite 0.8s;
`;

const EnhancedChannelsContainer = styled(ChannelsContainer)`
  width: 100%;
  display: flex;
  justify-content: space-around;
  gap: 60px;
  z-index: 5;
  position: relative;
`;

const EnhancedChannelItem = styled(ChannelItem)`
  width: 280px;
  height: 280px;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: rgba(5, 10, 25, 0.8);
  position: relative;
  opacity: 0;
  animation: ${rotateIn} 1.5s cubic-bezier(0.19, 1, 0.22, 1) forwards;
  animation-delay: ${props => props.delay || '0s'};
  will-change: transform;
  transition: transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  cursor: pointer;
  
  &::before, &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    z-index: -1;
  }
  
  &::before {
    top: -8px;
    left: -8px;
    right: -8px;
    bottom: -8px;
    border: 2px solid ${props => props.borderColor || 'rgba(100, 182, 255, 0.3)'};
    animation: ${pulseGlow} 5s infinite;
    transition: all 0.5s ease;
  }
  
  &::after {
    top: -3px;
    left: -3px;
    right: -3px;
    bottom: -3px;
    border: 1px solid ${props => props.accentColor || 'rgba(0, 255, 255, 0.5)'};
    opacity: 0.8;
    transition: all 0.5s ease;
  }
  
  &:hover {
    transform: translateY(-20px) scale(1.1);
    transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    z-index: 10;
    
    &::before {
      box-shadow: 0 0 50px ${props => props.accentColor || 'rgba(0, 255, 255, 0.7)'};
      border-color: ${props => props.accentColor || 'rgba(0, 255, 255, 1)'};
      border-width: 3px;
      top: -12px;
      left: -12px;
      right: -12px;
      bottom: -12px;
    }
    
    &::after {
      opacity: 1;
      border-width: 2px;
    }
    
    .channel-hover-effect {
      animation: ${energyBurst} 1.2s ease-out;
    }
    
    .channel-name {
      letter-spacing: 4px;
      text-shadow: 0 0 20px ${props => props.accentColor || 'rgba(0, 255, 255, 0.8)'};
    }
    
    .whatsapp-icon {
      animation: ${iconPop} 0.5s ease forwards;
      filter: drop-shadow(0 0 20px ${props => props.accentColor || 'rgba(0, 255, 255, 1)'});
    }
    
    .phone-icon {
      animation: ${phoneIconPop} 0.5s ease forwards;
      filter: drop-shadow(0 0 20px ${props => props.accentColor || 'rgba(0, 255, 255, 1)'});
    }
    
    .sms-icon {
      animation: ${smsIconPop} 0.5s ease forwards;
      filter: drop-shadow(0 0 20px ${props => props.accentColor || 'rgba(0, 230, 230, 1)'});
    }
    
    .tech-circle {
      border-color: ${props => props.accentColor || 'rgba(0, 255, 255, 0.7)'};
      opacity: 0.8;
    }
    
    .orbit-circle {
      opacity: 0.9;
      border-width: 2px;
    }
    
    .orbit-dot {
      transform: scale(1.5) translateY(-50%);
      box-shadow: 0 0 20px ${props => props.accentColor || 'rgba(0, 255, 255, 1)'};
    }
  }
`;

const CircleOrbit = styled.div`
  position: absolute;
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  border-radius: 50%;
  border: 1px dashed ${props => props.color || 'rgba(100, 182, 255, 0.2)'};
  opacity: 0.5;
  animation: spin ${props => props.duration}s linear infinite;
  
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const OrbitDot = styled.div`
  position: absolute;
  width: 10px;
  height: 10px;
  background-color: ${props => props.color || '#00FFFF'};
  border-radius: 50%;
  top: 0;
  left: calc(50% - 5px);
  transform: translateY(-50%);
  box-shadow: 0 0 10px ${props => props.color || '#00FFFF'};
`;

const EnhancedChannelIconContainer = styled(ChannelIconContainer)`
  width: 120px;
  height: 120px;
  margin-bottom: 25px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  animation: ${floatAnimation} 4s ease-in-out infinite;
  transition: all 0.4s ease;
`;

const EnhancedChannelIcon = styled(ChannelIcon)`
  width: 120px;
  height: 120px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  background: rgba(5, 10, 25, 0.8);
  border-radius: 50%;
  box-shadow: 0 0 30px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -10%;
    left: -10%;
    width: 120%;
    height: 120%;
    background: linear-gradient(
      135deg, 
      rgba(255, 255, 255, 0.02) 0%, 
      rgba(255, 255, 255, 0.1) 50%, 
      rgba(255, 255, 255, 0.02) 100%
    );
    opacity: 0.5;
    z-index: 1;
    pointer-events: none;
  }
`;

const IconWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2;
  
  svg {
    width: 60px;
    height: 60px;
    filter: drop-shadow(0 0 10px ${props => props.color || 'rgba(0, 255, 255, 0.8)'});
    transition: filter 0.4s ease, transform 0.4s ease;
  }
`;

const EnhancedChannelName = styled(ChannelName)`
  font-size: 28px;
  margin-top: 20px;
  background: linear-gradient(90deg, ${props => props.startColor || '#64B6FF'}, ${props => props.endColor || '#00FFFF'});
  background-size: 200% auto;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 600;
  letter-spacing: 2px;
  animation: ${holographicShimmer} 5s linear infinite;
  transition: letter-spacing 0.4s ease, text-shadow 0.4s ease;
  position: relative;
`;

const TechCircle = styled.div`
  position: absolute;
  width: ${props => props.size || '320px'};
  height: ${props => props.size || '320px'};
  border-radius: 50%;
  border: 1px solid ${props => props.color || 'rgba(100, 182, 255, 0.2)'};
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  opacity: 0.5;
  z-index: 1;
`;

const HoverEffect = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    ${props => props.color || 'rgba(0, 255, 255, 0.4)'} 0%,
    transparent 70%
  );
  opacity: 0;
  z-index: 0;
  pointer-events: none;
`;


const PresentationSlide19 = () => {
  const { lang } = useLanguage();
  const t = getTranslation('slide19', lang);
  const location = useLocation();
  
  // SMS só aparece na rota /presentation/19 (inglês)
  const showSMS = location.pathname.startsWith('/presentation');

  return (
    <SlideWrapper>
      <SlideTitle>
        {t.title}
        <TitleCodeDecoration className="top-left" />
        <TitleCodeDecoration className="top-right" />
      </SlideTitle>
      
      <ContentPreview>
        
        <EnhancedChannelsContainer>
          {/* Canal WhatsApp */}
          <EnhancedChannelItem 
            delay="0.5s" 
            borderColor="rgba(37, 211, 102, 0.5)" 
            accentColor="rgba(37, 211, 102, 0.8)"
          >
            <HoverEffect className="channel-hover-effect" color="rgba(37, 211, 102, 0.4)" />
            
            <CircleOrbit className="orbit-circle" size={330} color="rgba(37, 211, 102, 0.2)" duration={40}>
              <OrbitDot className="orbit-dot" color="#25D366" />
            </CircleOrbit>
            
            <TechCircle className="tech-circle" size="290px" color="rgba(37, 211, 102, 0.2)" />
            
            <EnhancedChannelIconContainer>
              <EnhancedChannelIcon>
                <IconWrapper className="icon-wrapper" color="rgba(37, 211, 102, 0.8)">
                  <FaWhatsapp className="whatsapp-icon" style={{ color: '#25D366' }} />
                </IconWrapper>
              </EnhancedChannelIcon>
            </EnhancedChannelIconContainer>
            
            <EnhancedChannelName className="channel-name" startColor="#25D366" endColor="#30E675">
              {t.whatsapp}
            </EnhancedChannelName>
          </EnhancedChannelItem>
          
          {/* Canal Ligação */}
          <EnhancedChannelItem 
            delay="0.8s" 
            borderColor="rgba(100, 182, 255, 0.5)" 
            accentColor="rgba(0, 255, 255, 0.8)"
          >
            <HoverEffect className="channel-hover-effect" color="rgba(0, 255, 255, 0.4)" />
            
            <CircleOrbit className="orbit-circle" size={330} color="rgba(100, 182, 255, 0.2)" duration={35}>
              <OrbitDot className="orbit-dot" color="#64B6FF" />
            </CircleOrbit>
            
            <TechCircle className="tech-circle" size="290px" color="rgba(100, 182, 255, 0.2)" />
            
            <EnhancedChannelIconContainer>
              <EnhancedChannelIcon>
                <IconWrapper className="icon-wrapper" color="rgba(100, 182, 255, 0.8)">
                  <FaPhone className="phone-icon" style={{ transform: 'scaleX(-1)', color: '#64B6FF' }} />
                </IconWrapper>
              </EnhancedChannelIcon>
            </EnhancedChannelIconContainer>
            
            <EnhancedChannelName className="channel-name" startColor="#64B6FF" endColor="#00FFFF">
              {t.call}
            </EnhancedChannelName>
          </EnhancedChannelItem>
          
          {/* Canal SMS - apenas em /presentation/19 */}
          {showSMS && (
            <EnhancedChannelItem 
              delay="1.1s" 
              borderColor="rgba(0, 200, 200, 0.5)" 
              accentColor="rgba(0, 230, 230, 0.8)"
            >
              <HoverEffect className="channel-hover-effect" color="rgba(0, 230, 230, 0.4)" />
              
              <CircleOrbit className="orbit-circle" size={330} color="rgba(0, 200, 200, 0.2)" duration={38}>
                <OrbitDot className="orbit-dot" color="#00C8C8" />
              </CircleOrbit>
              
              <TechCircle className="tech-circle" size="290px" color="rgba(0, 200, 200, 0.2)" />
              
              <EnhancedChannelIconContainer>
                <EnhancedChannelIcon>
                  <IconWrapper className="icon-wrapper" color="rgba(0, 200, 200, 0.8)">
                    <FaSms className="sms-icon" style={{ color: '#00E6E6' }} />
                  </IconWrapper>
                </EnhancedChannelIcon>
              </EnhancedChannelIconContainer>
              
              <EnhancedChannelName className="channel-name" startColor="#00C8C8" endColor="#00FFFF">
                {t.sms}
              </EnhancedChannelName>
            </EnhancedChannelItem>
          )}
        </EnhancedChannelsContainer>
      </ContentPreview>
    </SlideWrapper>
  );
};

export default PresentationSlide19;
